import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { ApiConsumer } from '../../../service/api-consumer';

import { DeviceListRequestParams } from '../../../interface/request/DeviceListRequestParams';
import { DeviceListResponse } from '../../../interface/response/device-list/DeviceListResponse';

import { Warning } from '../../../enum/health-monitor/Warning';
import { SearchButton } from '../../../enum/health-monitor/SearchButton';
import { ColumnName } from '../../../enum/health-monitor/ColumnName';
import { HealthMonitorGridAccessor } from '../../../enum/health-monitor/HealthMonitorGridAccessor';
import { AutocompleteListParams } from '../../../interface/request/AutocompleteListParams';

interface DeviceList {
  deviceList: DeviceListResponse | null;
  isLoadingDeviceList: boolean;
  totalCount: number;
  totalOffline: number;
  totalOnline: number;
  totalWarnings: number;
  shellWarnings: number;
  moneyWarnings: number;
  printerWarnings: number;
  totalWarningsByType: number;
  activeSearchButton: SearchButton;
  activeCheckbox: string;
  countForPagination: number;
  currentPage: number;
  resultingPage: number;
  selectedRowsPerPage: number;
  lastPage: number;
  searchInput: string | null;
  sortColumn: ColumnName;
  columnToSearchBy: ColumnName;
  isDesc: boolean;
  autocompleteList: string[];
}

const initialState: DeviceList = {
  deviceList: null,
  isLoadingDeviceList: false,
  totalCount: 0,
  totalOffline: 0,
  totalOnline: 0,
  totalWarnings: 0,
  shellWarnings: 0,
  moneyWarnings: 0,
  printerWarnings: 0,
  totalWarningsByType: 0,
  activeSearchButton: SearchButton.ALL,
  activeCheckbox: '',
  countForPagination: 1,
  currentPage: 0,
  resultingPage: 0,
  selectedRowsPerPage: 20,
  lastPage: 1,
  searchInput: null,
  sortColumn: ColumnName.NAME,
  columnToSearchBy: ColumnName.NAME,
  isDesc: true,
  autocompleteList: [],
};

export const getDeviceList = createAsyncThunk('/deviceList', async (data?: DeviceListRequestParams) => {
  const response = await ApiConsumer.getDeviceList(
    data?.page,
    data?.size,
    data?.isDescending,
    data?.sortColumn,
    data?.searchColumn,
    data?.search,
    data?.warning?.slice(0, -1),
    data?.status,
  );

  return { ...response, page: data?.page };
});

export const getAutocompleteList = createAsyncThunk('/autocomplete', async (data: AutocompleteListParams) => {
  const response = await ApiConsumer.getAutocompleteList(data.search, data.column, data.maxSize);

  return response;
});

export const deviceListSlice = createSlice({
  name: 'deviceList',
  initialState,
  reducers: {
    setActiveCheckbox: (state, { payload }) => {
      if (payload !== '') {
        if (!state.activeCheckbox.includes(`${payload},`)) {
          state.activeCheckbox += `${payload},`;
        } else {
          state.activeCheckbox = state.activeCheckbox.replace(`${payload},`, '');
        }
      } else {
        state.activeCheckbox = '';
      }
    },
    setCurrentPage: (state, { payload }) => {
      state.currentPage = payload;
    },
    setLastPage: (state, { payload }) => {
      state.lastPage = payload;
    },
    setSearchInput: (state, { payload }) => {
      state.searchInput = payload;
    },
    clearAutocompleteList: (state) => {
      state.autocompleteList = [];
    },
    setActiveSearchButton: (state, { payload }) => {
      state.activeSearchButton = payload;
    },
    setSortColumn: (state, { payload }) => {
      switch (payload) {
        case HealthMonitorGridAccessor.LOCATION_NAME:
          state.sortColumn = ColumnName.BETSHOP;
          break;
        case HealthMonitorGridAccessor.STATUS:
          state.sortColumn = ColumnName.SHELL_STATUS;
          break;
        default:
          state.sortColumn = ColumnName.NAME;
          break;
      }
    },
    setColumnToSearchBy: (state, { payload }) => {
      state.columnToSearchBy = payload;
    },
    setIsDesc: (state, { payload }) => {
      state.isDesc = payload;
    },
    setColumnOrder: (state, { payload }) => {
      switch (payload) {
        case HealthMonitorGridAccessor.LOCATION_NAME:
          if (state.sortColumn === ColumnName.BETSHOP) {
            state.isDesc = !state.isDesc;
          } else {
            state.isDesc = true;
          }
          break;
        case HealthMonitorGridAccessor.STATUS:
          if (state.sortColumn === ColumnName.SHELL_STATUS) {
            state.isDesc = !state.isDesc;
          } else {
            state.isDesc = true;
          }
          break;
        default:
          if (state.sortColumn === ColumnName.NAME) {
            state.isDesc = !state.isDesc;
          } else {
            state.isDesc = true;
          }
          break;
      }
    },
    setSelectedRowsPerPage: (state, { payload }) => {
      state.selectedRowsPerPage = payload;

      const tempLastPage = Math.ceil(state.countForPagination / state.selectedRowsPerPage) - 1;

      if (state.currentPage > tempLastPage) {
        state.currentPage = tempLastPage;
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getDeviceList.fulfilled, (state, { payload }) => {
      state.deviceList = payload;

      state.totalCount = payload.totalCount;
      state.totalOffline = payload.totalOffline;
      state.totalOnline = payload.totalOnline;
      state.totalWarnings = payload.totalWarnings;

      let totalWarningsByType = JSON.parse(payload.totalWarningsByType);
      state.shellWarnings = totalWarningsByType[Warning.SHELL] || 0;
      state.moneyWarnings = totalWarningsByType[Warning.MONEY] || 0;
      state.printerWarnings = totalWarningsByType[Warning.PRINTER] || 0;

      state.totalWarningsByType = state.shellWarnings + state.moneyWarnings + state.printerWarnings;

      switch (state.activeSearchButton) {
        case SearchButton.WARNING:
          state.countForPagination = state.totalWarnings;
          break;
        case SearchButton.ONLINE:
          state.countForPagination = state.totalOnline;
          break;
        case SearchButton.OFFLINE:
          state.countForPagination = state.totalOffline;
          break;
        default:
          state.countForPagination = state.totalCount;
          break;
      }

      if (
        state.activeCheckbox.includes(`${Warning.SHELL}`) ||
        state.activeCheckbox.includes(`${Warning.MONEY}`) ||
        state.activeCheckbox.includes(`${Warning.PRINTER}`)
      ) {
        state.countForPagination = 0;

        if (state.activeCheckbox.includes(`${Warning.SHELL}`)) {
          state.countForPagination += state.shellWarnings;
        }

        if (state.activeCheckbox.includes(`${Warning.MONEY}`)) {
          state.countForPagination += state.moneyWarnings;
        }

        if (state.activeCheckbox.includes(`${Warning.PRINTER}`)) {
          state.countForPagination += state.printerWarnings;
        }
      }

      state.lastPage = Math.ceil(state.countForPagination / state.selectedRowsPerPage) || state.lastPage;
      state.currentPage = payload.page || state.currentPage;
      state.resultingPage = state.currentPage;
      state.isLoadingDeviceList = false;
    });

    builder.addCase(getDeviceList.pending, (state) => {
      state.isLoadingDeviceList = true;
    });

    builder.addCase(getDeviceList.rejected, (state) => {
      state.isLoadingDeviceList = false;
    });

    builder.addCase(getAutocompleteList.fulfilled, (state, { payload }) => {
      state.autocompleteList = payload.results;
      // state.isLoadingDeviceList = false;
    });

    builder.addCase(getAutocompleteList.pending, (state) => {
      // state.isLoadingDeviceList = true;
    });

    builder.addCase(getAutocompleteList.rejected, (state) => {
      // state.isLoadingDeviceList = false;
    });
  },
});

export const {
  setActiveCheckbox,
  setCurrentPage,
  setLastPage,
  setSearchInput,
  clearAutocompleteList,
  setActiveSearchButton,
  setSortColumn,
  setIsDesc,
  setColumnToSearchBy,
  setColumnOrder,
  setSelectedRowsPerPage,
} = deviceListSlice.actions;

export default deviceListSlice.reducer;
